import React, { useState } from "react";
import { FaInstagram, FaLinkedin } from "react-icons/fa";

export default function ContactMe() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();

    setIsSubmitting(true);
    setFeedback(null);

    const formData = {
      name: event.target.name.value,
      email: event.target.email.value,
      message: event.target.message.value,
    };

    try {
      const response = await fetch(
        "https://portfoliojoeybackend.vercel.app/api/contact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send the message.");
      }

      setFeedback({
        type: "success",
        message: "Your message has been sent successfully!",
      });

      event.target.reset();
    } catch (error) {
      setFeedback({ type: "error", message: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => setFeedback(null);

  return (
    <div className="min-h-screen overflow-hidden">
      <div
        className="relative w-full min-h-screen"
        style={{
          backgroundImage:
            "linear-gradient(135deg, #12100d 0%, #201a13 50%, #0e0c0a 100%)",
          backgroundAttachment: "fixed",
        }}
      >
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-[0.04]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 25% 25%, #c9a876 1px, transparent 1px),
                             radial-gradient(circle at 75% 75%, #c9a876 1px, transparent 1px)`,
              backgroundSize: "60px 60px",
            }}
          ></div>
        </div>

        {/* Mystical Floating Elements — softened, no blinking */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-1 h-1 bg-[#c9a876] rounded-full"></div>
          <div className="absolute top-40 right-20 w-0.5 h-0.5 bg-[#e8dcc4] rounded-full"></div>
          <div className="absolute bottom-40 left-1/4 w-1 h-1 bg-[#c9a876] rounded-full"></div>
          <div className="absolute top-60 right-1/3 w-0.5 h-0.5 bg-[#e8dcc4] rounded-full"></div>
          <div className="absolute top-80 left-1/2 w-1 h-1 bg-[#c9a876] rounded-full"></div>
          <div className="absolute bottom-60 right-1/4 w-0.5 h-0.5 bg-[#e8dcc4] rounded-full"></div>
        </div>

        {/* Page Header */}
        <div className="relative py-20 text-center">
          {/* Decorative Line */}
          <div className="flex items-center justify-center space-x-4 mb-8">
            <div className="w-12 h-px bg-[#c9a876]"></div>
            <div className="w-2 h-2 border border-[#c9a876] rotate-45"></div>
            <div className="w-12 h-px bg-[#c9a876]"></div>
          </div>

          <h1 className="hero-display text-5xl sm:text-6xl md:text-7xl font-light text-[#f5f0e6] leading-none tracking-wide mb-6">
            Contact Me
          </h1>

          <div className="flex items-center justify-center space-x-6 mb-8">
            <div className="w-16 h-px bg-[#e8dcc4] opacity-40"></div>
            <h2 className="hero-body text-2xl md:text-3xl font-light text-[#c9a876] tracking-[0.3em] uppercase">
              Get In Touch
            </h2>
            <div className="w-16 h-px bg-[#e8dcc4] opacity-40"></div>
          </div>

          {/* Decorative Bottom Element */}
          <div className="flex items-center justify-center space-x-3 mt-12">
            <div className="w-8 h-px bg-[#c9a876]"></div>
            <div className="w-1 h-1 bg-[#c9a876] rounded-full"></div>
            <div className="w-4 h-px bg-[#c9a876]"></div>
            <div className="w-1 h-1 bg-[#c9a876] rounded-full"></div>
          </div>
        </div>

        {/* Main Content */}
        <div className="relative container mx-auto px-6 sm:px-12 lg:px-24 py-8">
          <div className="flex flex-col lg:flex-row items-start gap-12">
            {/* Left Section - Contact Info */}
            <div className="w-full lg:w-1/2">
              <div className="relative bg-black/20 backdrop-blur-sm border border-[#c9a876]/20 p-8">
                {/* Title with decorative line */}
                <div className="flex items-center space-x-3 mb-8">
                  <div className="w-8 h-px bg-[#c9a876]"></div>
                  <h3 className="hero-display text-3xl font-light text-[#f5f0e6] tracking-wide">
                    Get in Touch
                  </h3>
                </div>

                <div className="space-y-8">
                  {/* Email */}
                  <div className="group text-left">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-4 h-px bg-[#e8dcc4] opacity-30"></div>
                      <p className="hero-body text-sm font-light text-gray-300 tracking-widest uppercase">
                        Email
                      </p>
                    </div>
                    <a
                      href="mailto:josephmunnangi007@gmail.com"
                      className="hero-body text-lg text-[#c9a876] hover:text-white transition-colors duration-300 font-light tracking-wide"
                    >
                      josephmunnangi007@gmail.com
                    </a>
                  </div>

                  {/* Phone */}
                  <div className="group text-left">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-4 h-px bg-[#e8dcc4] opacity-30"></div>
                      <p className="hero-body text-sm font-light text-gray-300 tracking-widest uppercase">
                        Phone
                      </p>
                    </div>
                    <a
                      href="tel:+917901345269"
                      className="hero-body text-lg text-[#c9a876] hover:text-white transition-colors duration-300 font-light tracking-wide"
                    >
                      +91 7901345269
                    </a>
                  </div>

                  {/* Social Media */}
                  <div className="group">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-4 h-px bg-[#e8dcc4] opacity-30"></div>
                      <p className="hero-body text-sm font-light text-gray-300 tracking-widest uppercase">
                        Social Media
                      </p>
                    </div>
                    <div className="flex gap-6">
                      <a
                        href="https://instagram.com/joey007_"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#c9a876] text-2xl hover:text-white transition-colors duration-300 hover:scale-110 transform"
                      >
                        <FaInstagram />
                      </a>
                      <a
                        href="https://in.linkedin.com/in/munnangi-joseph-sukumar-63a7b7259"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#c9a876] text-2xl hover:text-white transition-colors duration-300 hover:scale-110 transform"
                      >
                        <FaLinkedin />
                      </a>
                    </div>
                  </div>
                </div>

                {/* Decorative Corner Elements */}
                <div className="absolute -top-2 -left-2 w-4 h-4 border-l-2 border-t-2 border-[#c9a876] opacity-60"></div>
                <div className="absolute -top-2 -right-2 w-4 h-4 border-r-2 border-t-2 border-[#c9a876] opacity-60"></div>
                <div className="absolute -bottom-2 -left-2 w-4 h-4 border-l-2 border-b-2 border-[#c9a876] opacity-60"></div>
                <div className="absolute -bottom-2 -right-2 w-4 h-4 border-r-2 border-b-2 border-[#c9a876] opacity-60"></div>
              </div>
            </div>

            {/* Right Section - Contact Form */}
            <div className="w-full lg:w-1/2">
              <div className="relative bg-black/20 backdrop-blur-sm border border-[#c9a876]/20 p-8">
                {/* Form Title */}
                <div className="flex items-center space-x-3 mb-8">
                  <div className="w-8 h-px bg-[#c9a876]"></div>
                  <h3 className="hero-display text-2xl font-light text-[#f5f0e6] tracking-wide">
                    Send Message
                  </h3>
                </div>

                <p className="hero-body text-gray-300 mb-8 font-light leading-relaxed">
                  Feel free to reach out to me for any inquiries or
                  collaboration opportunities.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name Field */}
                  <div>
                    <label
                      htmlFor="name"
                      className="hero-body block text-white mb-3 font-light tracking-wide"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="hero-body w-full p-4 bg-black/40 border border-[#c9a876]/30 text-white outline-none focus:border-[#c9a876] transition-colors duration-300 font-light"
                      placeholder="Enter your name"
                      required
                    />
                  </div>

                  {/* Email Field */}
                  <div>
                    <label
                      htmlFor="email"
                      className="hero-body block text-white mb-3 font-light tracking-wide"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="hero-body w-full p-4 bg-black/40 border border-[#c9a876]/30 text-white outline-none focus:border-[#c9a876] transition-colors duration-300 font-light"
                      placeholder="Enter your email"
                      required
                    />
                  </div>

                  {/* Message Field */}
                  <div>
                    <label
                      htmlFor="message"
                      className="hero-body block text-white mb-3 font-light tracking-wide"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows="6"
                      className="hero-body w-full p-4 bg-black/40 border border-[#c9a876]/30 text-white outline-none focus:border-[#c9a876] transition-colors duration-300 font-light resize-none"
                      placeholder="Enter your message"
                      required
                    ></textarea>
                  </div>

                  {/* Submit Button */}
                  <div className="flex items-center space-x-4 pt-4">
                    <div className="w-6 h-px bg-[#c9a876]"></div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`hero-body px-8 py-3 bg-[#c9a876]/20 border border-[#c9a876] text-[#c9a876] font-light tracking-widest uppercase hover:bg-[#c9a876] hover:text-black transition-all duration-300 ${
                        isSubmitting
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:scale-105"
                      }`}
                    >
                      {isSubmitting ? "Submitting..." : "Send Message"}
                    </button>
                    <div className="w-4 h-px bg-[#c9a876]"></div>
                  </div>
                </form>

                {/* Decorative Corner Elements */}
                <div className="absolute -top-2 -left-2 w-4 h-4 border-l-2 border-t-2 border-[#c9a876] opacity-60"></div>
                <div className="absolute -top-2 -right-2 w-4 h-4 border-r-2 border-t-2 border-[#c9a876] opacity-60"></div>
                <div className="absolute -bottom-2 -left-2 w-4 h-4 border-l-2 border-b-2 border-[#c9a876] opacity-60"></div>
                <div className="absolute -bottom-2 -right-2 w-4 h-4 border-r-2 border-b-2 border-[#c9a876] opacity-60"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section Divider */}
        <div className="relative py-16 text-center">
          <div className="flex items-center justify-center space-x-4">
            <div className="w-20 h-px bg-[#c9a876]"></div>
            <div className="w-2 h-2 bg-[#c9a876] rounded-full"></div>
            <div className="w-8 h-px bg-[#e8dcc4] opacity-40"></div>
            <div className="w-2 h-2 bg-[#c9a876] rounded-full"></div>
            <div className="w-20 h-px bg-[#c9a876]"></div>
          </div>
        </div>

        {/* Bottom Fade */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/20 to-transparent"></div>
      </div>

      {/* Loading Spinner */}
      {isSubmitting && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-50">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#c9a876]"></div>
            <div className="absolute inset-0 rounded-full border-2 border-[#c9a876]/20"></div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {feedback && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-50">
          <div className="relative bg-black/40 backdrop-blur-md border border-[#c9a876]/30 p-8 text-center max-w-md mx-4">
            {/* Title with decorative line */}
            <div className="flex items-center justify-center space-x-3 mb-6">
              <div className="w-6 h-px bg-[#c9a876]"></div>
              <h3 className="hero-display text-2xl font-light text-[#f5f0e6] tracking-wide">
                {feedback.type === "success" ? "Success" : "Error"}
              </h3>
              <div className="w-6 h-px bg-[#c9a876]"></div>
            </div>

            <div className="text-4xl mb-4">
              {feedback.type === "success" ? "🎉" : "⚠️"}
            </div>

            <p className="hero-body mb-8 text-gray-300 font-light leading-relaxed">
              {feedback.message}
            </p>

            <div className="flex items-center justify-center space-x-4">
              <div className="w-4 h-px bg-[#c9a876]"></div>
              <button
                onClick={closeModal}
                className="hero-body px-6 py-2 bg-[#c9a876]/20 border border-[#c9a876] text-[#c9a876] font-light tracking-widest uppercase hover:bg-[#c9a876] hover:text-black transition-all duration-300"
              >
                Close
              </button>
              <div className="w-4 h-px bg-[#c9a876]"></div>
            </div>

            {/* Decorative Corner Elements */}
            <div className="absolute -top-2 -left-2 w-4 h-4 border-l-2 border-t-2 border-[#c9a876]"></div>
            <div className="absolute -top-2 -right-2 w-4 h-4 border-r-2 border-t-2 border-[#c9a876]"></div>
            <div className="absolute -bottom-2 -left-2 w-4 h-4 border-l-2 border-b-2 border-[#c9a876]"></div>
            <div className="absolute -bottom-2 -right-2 w-4 h-4 border-r-2 border-b-2 border-[#c9a876]"></div>
          </div>
        </div>
      )}
    </div>
  );
}
